import { useState, useEffect } from "react";
import { fetchAdminProducts } from "../services/productsService";

/**
 * Maps a raw product item from the API to the internal product shape used
 * by the admin UI.
 */
function mapProduct(item) {
  return {
    id: item.id,
    product_id: item.product_id,
    name: item.product_name,
    brand: item.brand_name,
    category: item.category || "General",
    gtin: item.gtin || "N/A",
    status: item.status,
    emoji: "📦",
    bg: "rgba(55,190,176,.12)",
    imageUrl: item.image_url || null,
    createdDate: item.created_at,
    createdBy: "admin",
    description: "",
    ingredients: "",
    nutrients: "",
    approvedBy: null,
    approvedDate: null,
    rejectReason: null,
    isReview: false,
    ageGroup: item.age_group || null,
    product_score_v2: item.product_score_v2 || null,
  };
}

/**
 * Maps a raw review task from the API to the internal product shape so
 * it renders correctly in the shared products table.
 *
 * The "category" column is reused to display the GTIN for reviews.
 */
export function mapReview(item) {
  const sub = item.submission || {};
  const gtin = sub.gtin || "N/A";
  const fileCount = Array.isArray(sub.files) ? sub.files.length : (sub.file_count || 0);

  return {
    // Review-task identifiers
    id: item.id,
    product_id: item.product_id,
    submission_id: item.submission_id,

    // Display fields
    name: sub.product_name || "Unknown Product",
    brand: sub.brand_name || "Unknown Brand",
    category: `GTIN: ${gtin}`,
    gtin: gtin,

    // Always "pending" for needs_review tasks shown in the Pending tab
    status: "pending",

    emoji: "🔍",
    bg: "rgba(55,190,176,.12)",
    imageUrl: item.image_url || sub.image_url || null,
    createdDate: sub.submitted_at || item.created_at,
    createdBy: sub.agent_id || "Agent",
    description: `GTIN: ${gtin} · ${fileCount} file${fileCount !== 1 ? "s" : ""} submitted`,

    // These are populated later by useReviewDetail when the user clicks View
    ingredients: "",
    nutrients: "",
    files: [],

    approvedBy: null,
    approvedDate: null,
    rejectReason: null,

    // Flag so pages know this row represents a review task
    isReview: true,
    ageGroup: sub.age_group || null,
    product_score_v2: item.product_score_v2 || sub.product_score_v2 || null,
  };
}

/**
 * Loads products (or reviews) for the active tab and exposes them via the
 * shared setProducts context setter.
 *
 * Tab → API behaviour:
 *   "all"      → GET /admin/products              (no status filter)
 *   "approved" → GET /admin/products?status=approved
 *   "rejected" → GET /admin/products?status=rejected
 *   "pending"  → GET /admin/products?status=pending
 *
 * @param {Function} setProducts  - context setter from DashboardLayout
 * @param {Function} addToast     - context toast helper
 * @param {string}   statusFilter - active tab id: "all"|"approved"|"rejected"|"pending"
 */
export function useProductsList(setProducts, addToast, statusFilter = "all", page = 1, limit = 10, search = "") {
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadTab() {
      setLoading(true);
      try {
        const apiStatus = statusFilter === "all" ? null : statusFilter;
        const apiSearch = search.trim() !== "" ? search.trim() : null;
        const productsData = await fetchAdminProducts({ page, limit, status: apiStatus, search: apiSearch });
        if (cancelled) return;
        const mappedProducts = productsData?.items ? productsData.items.map(mapProduct) : [];
        setProducts(mappedProducts);
        setTotal(productsData?.pagination?.total || 0);
      } catch (err) {
        if (!cancelled) addToast(err.message || "Failed to load products", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    const timer = setTimeout(() => loadTab(), search ? 300 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, limit, search]);

  return { loading, total };
}

